import React from 'react';

const EventCard = ({ event, onClick }) => {
  const segmentCount = event.segments?.length || 0;
  const openCount = event.segments?.filter(
    s => !s.reg_deadline || new Date() <= new Date(s.reg_deadline)
  ).length || 0;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const dateDisplay = event.event_date ? formatDate(event.event_date) : null;

  // Category label — capitalize first letter
  const categoryLabel = event.category && event.category !== 'other'
    ? event.category.charAt(0).toUpperCase() + event.category.slice(1)
    : null;

  return (
    <div
      className="group relative bg-white/[0.02] rounded-[1.5rem] border border-white/[0.07] overflow-hidden cursor-pointer hover:border-sky-500/30 hover:bg-white/[0.04] transition-all duration-500 shadow-2xl flex flex-col h-full"
      onClick={onClick}
    >
      {/* Banner */}
      <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
        <img
          src={event.banner || 'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&q=80&w=1200'}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Live badge */}
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-xl border border-white/10 px-2.5 py-1.5 rounded-full">
          <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-[9px] font-black text-white uppercase tracking-widest">Live</span>
        </div>

        {/* Open count badge */}
        <div className="absolute top-3 right-3 bg-sky-500 text-black text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest">
          {openCount} open
        </div>

        {/* Category badge — bottom left of image */}
        {categoryLabel && (
          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm border border-white/10 text-[8px] font-black text-white/70 px-2 py-1 rounded-full uppercase tracking-widest">
            {categoryLabel}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow">
        {/* Organiser name — shows if set */}
        {event.organiser_name ? (
          <div className="flex items-center gap-1.5 mb-2">
            {event.organiser_logo && (
              <img src={event.organiser_logo} alt={event.organiser_name} className="w-4 h-4 rounded object-contain bg-white p-0.5 shrink-0" />
            )}
            <p className="text-[8px] font-black text-gray-500 uppercase tracking-[0.3em] truncate">{event.organiser_name}</p>
          </div>
        ) : (
          <p className="text-[8px] font-black text-sky-500 uppercase tracking-[0.35em] mb-2">Verified Event</p>
        )}

        {/* Event name */}
        <h3 className="text-lg font-black text-white uppercase tracking-tight leading-tight mb-3">
          {event.name}
        </h3>

        {/* Description */}
        {event.description && (
          <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4">
            {event.description}
          </p>
        )}

        {/* Date & Venue */}
        {(dateDisplay || event.venue) && (
          <div className="space-y-2 mb-4">
            {dateDisplay && (
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xs font-semibold text-gray-300">{dateDisplay}</span>
              </div>
            )}
            {event.venue && (
              <div className="flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-xs font-semibold text-gray-300 truncate">{event.venue}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex-grow" />

        {/* CTA row */}
        <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm font-black text-white">{segmentCount}</p>
              <p className="text-[8px] text-gray-600 uppercase font-bold tracking-widest">Segments</p>
            </div>
            <div className="w-px h-6 bg-white/10" />
            <div>
              <p className="text-sm font-black text-sky-400">{openCount}</p>
              <p className="text-[8px] text-gray-600 uppercase font-bold tracking-widest">Accepting</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-400 group-hover:text-white group-hover:gap-2.5 transition-all duration-300">
            <span className="text-[10px] font-black uppercase tracking-widest">Explore</span>
            <span className="text-sm">→</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;